import { writable, get } from 'svelte/store';

export type ChatRole = 'user' | 'assistant';

export interface ChatMessage {
  role: ChatRole;
  content: string;
  timestamp: number;
  chat_qa_id?: string; // UUID for this Q&A pair (generated on client)
}

export interface ChatMeta {
  customer: string;
  selectionType: 'projects' | 'documents';
  ids: string[];
}

export interface ChatRecord {
  chat_id: string;
  meta: ChatMeta;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
}

type ChatState = Record<string, ChatRecord>;

const STORAGE_KEY_PREFIX = 'chatStoreV1';

// Track current user context for scoped storage
let currentUserId: string | null = null;

function getStorageKey(): string {
  // Scope storage by user ID to prevent cross-user data leakage
  return currentUserId ? `${STORAGE_KEY_PREFIX}_${currentUserId}` : STORAGE_KEY_PREFIX;
}

function safeLoadFromStorage(): ChatState {
  try {
    const raw = localStorage.getItem(getStorageKey()) || '{}';
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as ChatState;
    }
  } catch (_) {
    // ignore
  }
  return {};
}

function safeSaveToStorage(state: ChatState): void {
  try {
    localStorage.setItem(getStorageKey(), JSON.stringify(state));
  } catch (_) {
    // ignore quota/security errors
  }
}

/**
 * Clean up legacy unscoped storage key to prevent data leakage.
 * This removes the old global 'chatStoreV1' key that was shared by all users.
 */
function cleanupLegacyStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_PREFIX);
  } catch (_) {
    // ignore
  }
}

function createChatStore() {
  const initial: ChatState = safeLoadFromStorage();
  const { subscribe, update, set } = writable<ChatState>(initial);

  function persist(next: ChatState) {
    safeSaveToStorage(next);
  }

  return {
    subscribe,
    /**
     * Set the user context for scoped chat storage.
     * This switches the storage key to be user-specific and reloads from storage.
     * Must be called when user changes (login, logout, or user switch).
     * @param userId - The user's unique identifier (object_id), or null for no user
     */
    setUserContext: (userId: string | null) => {
      const previousUserId = currentUserId;
      currentUserId = userId;
      
      // Clean up legacy unscoped storage on first user context set
      if (previousUserId === null && userId !== null) {
        cleanupLegacyStorage();
      }
      
      // Reload from the new user-scoped storage
      const fresh = safeLoadFromStorage();
      set(fresh);
    },
    reset: () => {
      set({});
      persist({});
    },
    ensureChatRecord: (chat_id: string, meta: ChatMeta): ChatRecord => {
      let created: ChatRecord | undefined;
      update((state) => {
        const existing = state[chat_id];
        if (existing) {
          created = existing;
          return state;
        }
        const now = Date.now();
        const record: ChatRecord = {
          chat_id,
          meta: {
            customer: meta.customer,
            selectionType: meta.selectionType,
            ids: meta.ids.slice().sort(),
          },
          createdAt: now,
          updatedAt: now,
          messages: [],
        };
        const next = { ...state, [chat_id]: record };
        created = record;
        persist(next);
        return next;
      });
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return created!;
    },
    getChatRecord: (chat_id: string): ChatRecord | null => {
      const state = get(chatStore);
      return state[chat_id] || null;
    },
    getMessagesForChat: (chat_id: string): ChatMessage[] => {
      const state = get(chatStore);
      return state[chat_id]?.messages ?? [];
    },
    replaceMessagesForChat: (
      chat_id: string,
      messages: ChatMessage[],
      meta?: ChatMeta
    ): ChatRecord => {
      let result!: ChatRecord;
      update((state) => {
        const existing = state[chat_id];
        const now = Date.now();
        const record: ChatRecord = existing
          ? {
              ...existing,
              meta: meta
                ? {
                    customer: meta.customer,
                    selectionType: meta.selectionType,
                    ids: meta.ids.slice().sort(),
                  }
                : existing.meta,
              messages: messages.slice(),
              updatedAt: now,
            }
          : {
              chat_id,
              meta: meta || { customer: '', selectionType: 'projects', ids: [] },
              createdAt: now,
              updatedAt: now,
              messages: messages.slice(),
            };
        const next = { ...state, [chat_id]: record };
        result = record;
        persist(next);
        return next;
      });
      return result;
    },
    appendUserMessage: (chat_id: string, content: string, meta: ChatMeta, chat_qa_id?: string): ChatRecord => {
      let result!: ChatRecord;
      update((state) => {
        const now = Date.now();
        const existing = state[chat_id];
        const base: ChatRecord = existing || {
          chat_id,
          meta: { customer: meta.customer, selectionType: meta.selectionType, ids: meta.ids.slice().sort() },
          createdAt: now,
          updatedAt: now,
          messages: [],
        };
        const nextRecord: ChatRecord = {
          ...base,
          messages: [...base.messages, { role: 'user', content, timestamp: now, chat_qa_id }],
          updatedAt: now,
        };
        const next = { ...state, [chat_id]: nextRecord };
        result = nextRecord;
        persist(next);
        return next;
      });
      return result;
    },
    appendAssistantMessage: (chat_id: string, content: string, chat_qa_id?: string): ChatRecord | null => {
      let result: ChatRecord | null = null;
      update((state) => {
        const existing = state[chat_id];
        if (!existing) {
          return state;
        }
        const now = Date.now();
        const nextRecord: ChatRecord = {
          ...existing,
          messages: [...existing.messages, { role: 'assistant', content, timestamp: now, chat_qa_id }],
          updatedAt: now,
        };
        const next = { ...state, [chat_id]: nextRecord };
        result = nextRecord;
        persist(next);
        return next;
      });
      return result;
    },
    clearChatMessages: (chat_id: string): void => {
      update((state) => {
        const existing = state[chat_id];
        if (!existing) return state;
        const now = Date.now();
        const nextRecord: ChatRecord = {
          ...existing,
          messages: [],
          updatedAt: now,
        };
        const next = { ...state, [chat_id]: nextRecord };
        persist(next);
        return next;
      });
    },
    listChats: (): ChatRecord[] => {
      const state = get(chatStore);
      return Object.values(state).sort((a, b) => b.updatedAt - a.updatedAt);
    },
    deleteChat: (chat_id: string): void => {
      update((state) => {
        if (!state[chat_id]) return state;
        const next = { ...state };
        delete next[chat_id];
        persist(next);
        return next;
      });
    },
    refreshFromStorage: (): void => {
      const fresh = safeLoadFromStorage();
      set(fresh);
    },
  };
}

export const chatStore = createChatStore();

// Convenience re-exports for ergonomic imports
export const ensureChatRecord = chatStore.ensureChatRecord;
export const getChatRecord = chatStore.getChatRecord;
export const getMessagesForChat = chatStore.getMessagesForChat;
export const replaceMessagesForChat = chatStore.replaceMessagesForChat;
export const appendUserMessage = chatStore.appendUserMessage;
export const appendAssistantMessage = chatStore.appendAssistantMessage;
export const clearChatMessages = chatStore.clearChatMessages;
export const listChats = chatStore.listChats;
export const deleteChat = chatStore.deleteChat;
export const resetChatStore = chatStore.reset;
export const refreshChatStore = chatStore.refreshFromStorage;
export const setChatUserContext = chatStore.setUserContext;


